import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { sltAPI } from '../../services/api';
import { setSltViewAs } from '../../utils/sltViewAs';
import SLTLayout from './SLTLayout';
import { BRAND, cardStyle, formatDate, formatDateTime, TagBadge, StatPill } from './sltUi';

const SummaryCard = ({ label, value, sub, accent = '#75B9BE' }) => (
  <div style={{ ...cardStyle, borderTop: `6px solid ${accent}`, minWidth: 0, padding: '14px 18px' }}>
    <div style={{ color: BRAND.muted, fontSize: '0.74rem', fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase' }}>{label}</div>
    <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '1.7rem', fontWeight: 800, lineHeight: 1.1, marginTop: '4px' }}>{value ?? '—'}</div>
    {sub && <div style={{ color: BRAND.muted, fontSize: '0.78rem', marginTop: '3px' }}>{sub}</div>}
  </div>
);

const feedButtonStyle = {
  background: 'rgba(117, 185, 190, 0.18)',
  border: `1.5px solid ${BRAND.teal}`,
  borderRadius: '999px',
  color: BRAND.charcoal,
  cursor: 'pointer',
  fontFamily: 'Nunito, sans-serif',
  fontSize: '0.75rem',
  fontWeight: 800,
  padding: '4px 12px'
};

const SettingRow = ({ label, children }) => (
  <div style={{ borderBottom: '1px solid rgba(45,64,66,0.1)', display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'space-between', padding: '9px 0' }}>
    <span style={{ color: BRAND.muted, fontWeight: 700 }}>{label}</span>
    <span style={{ fontWeight: 800, textAlign: 'right' }}>{children}</span>
  </div>
);

const HISTORY_FIELD_LABELS = {
  age: 'Age',
  email: 'Email',
  readingAge: 'Reading age',
  senCondition: 'SEN condition',
  senConditions: 'SEN conditions',
  eal: 'EAL',
  primaryLanguage: 'Primary language',
  glossaryLanguageMode: 'Glossary language mode',
  audioNarrationEnabled: 'Audio narration',
  audioNarrationSpeed: 'Narration speed',
  noCustomScaffoldingRequired: 'No custom scaffolding required',
  customTutorialStructure: 'Custom tutorial structure',
  keyTermsPerMatchingPage: 'Key terms per matching page',
  tutorialFont: 'Tutorial font'
};

const describeValue = (value) => {
  if (value === undefined || value === null || value === '') return '—';
  if (Array.isArray(value)) return value.length ? value.join(', ') : '—';
  if (typeof value === 'boolean') return value ? 'On' : 'Off';
  return String(value);
};

const diffHistoryEntry = (entry = {}) => {
  const before = entry.before || {};
  const after = entry.after || {};
  return Object.keys(HISTORY_FIELD_LABELS)
    .filter((field) => JSON.stringify(before[field] ?? null) !== JSON.stringify(after[field] ?? null))
    .map((field) => ({
      label: HISTORY_FIELD_LABELS[field],
      from: describeValue(before[field]),
      to: describeValue(after[field])
    }));
};

const SLTStudentDetail = ({ user, onLogout }) => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;
    sltAPI.getStudentDetail(studentId)
      .then((res) => { if (isMounted) setData(res.data); })
      .catch((err) => {
        if (isMounted) setError(err?.response?.data?.error || 'Unable to load this student.');
      });
    return () => { isMounted = false; };
  }, [studentId]);

  const student = data?.student;
  const scaffolding = data?.scaffolding;
  const tutorials = useMemo(() => data?.tutorials || [], [data]);
  const summary = data?.summary;

  // Open the tutorial preview / progress report through the owning teacher's
  // read-only view (data endpoints are scoped to the teacher).
  const openInTeacherView = (tutorial, path) => {
    setSltViewAs({ teacherUid: tutorial.teacherUid, teacherName: tutorial.teacherName });
    navigate(path);
  };

  // Reverse-chronological activity: every digital tutorial attempt plus
  // worksheet/answer-key prints for homeworks this student is part of.
  // Repeated prints of the same worksheet collapse into one entry with a count.
  const activityFeed = useMemo(() => {
    const tutorialByHomework = new Map(tutorials.map((t) => [String(t.homeworkId), t]));
    const items = [];
    tutorials.forEach((t) => {
      (t.attempts || []).forEach((a) => {
        items.push({ type: 'attempt', when: a.startedAt, tutorial: t, attempt: a });
      });
    });

    const printGroups = new Map();
    (data?.printActivity || []).forEach((p) => {
      const key = `${p.action}|${p.homeworkId}`;
      const existing = printGroups.get(key);
      if (existing) {
        existing.count += 1;
        if (new Date(p.timestamp) > new Date(existing.when)) {
          existing.when = p.timestamp;
          existing.print = p;
        }
      } else {
        printGroups.set(key, {
          type: 'print',
          when: p.timestamp,
          print: p,
          count: 1,
          tutorial: tutorialByHomework.get(String(p.homeworkId)) || null
        });
      }
    });
    items.push(...printGroups.values());

    return items.sort((a, b) => new Date(b.when || 0) - new Date(a.when || 0));
  }, [data, tutorials]);

  return (
    <SLTLayout
      user={user}
      onLogout={onLogout}
      title={student ? student.name : 'Student'}
      subtitle={student ? `Age ${student.age} · reading age ${student.readingAge}${student.readingAgeLabel ? ` (${student.readingAgeLabel})` : ''} · added ${formatDate(student.createdAt)}` : undefined}
      breadcrumbs={[
        { label: 'Students', to: '/slt/students' },
        { label: student ? student.name : '…' }
      ]}
    >
      {error && <p style={{ color: '#B3261E', fontWeight: 700 }}>{error}</p>}
      {!data && !error && <p style={{ color: BRAND.muted }}>Loading student…</p>}

      {student && (
        <>
          <section aria-label="Enrolments" style={{ margin: '-8px 0 20px' }}>
            <div style={{ color: BRAND.muted, fontSize: '0.85rem', fontWeight: 800, letterSpacing: '0.04em', marginBottom: '6px', textTransform: 'uppercase' }}>
              Enrolments ({(data.enrolments || []).length})
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {(data.enrolments || []).map((e) => (
                <Link
                  key={e.studentDocId}
                  to={`/slt/teachers/${e.teacherUid}`}
                  style={{ background: 'rgba(117, 185, 190, 0.18)', border: `1.5px solid ${BRAND.teal}`, borderRadius: '999px', color: BRAND.charcoal, fontSize: '0.83rem', fontWeight: 800, padding: '5px 13px', textDecoration: 'none' }}
                  title={`Added ${formatDate(e.createdAt)}`}
                >
                  {e.className || 'Class'}{e.yearGroup ? ` · ${e.yearGroup}` : ''} — {e.teacherName}
                </Link>
              ))}
            </div>
          </section>

          {summary && (
            <section aria-label="Summary" style={{ display: 'grid', gap: '14px', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', marginBottom: '22px' }}>
              <SummaryCard label="Tutorials taken" value={summary.completedAttempts} sub={`${summary.attempts} attempt${summary.attempts === 1 ? '' : 's'} total`} accent={BRAND.coral} />
              <SummaryCard label="Success rate" value={summary.averageAccuracy != null ? `${summary.averageAccuracy}%` : '—'} sub="correct answers across attempts" accent="#1F7A3A" />
              <SummaryCard label="Assigned" value={summary.tutorialsAssigned} sub={`${summary.tutorialsCompleted} completed`} />
              <SummaryCard label="Worksheets printed" value={summary.worksheetsPrinted} sub="across their homeworks" />
              <SummaryCard label="Last active" value={summary.lastActivity ? formatDate(summary.lastActivity) : '—'} />
            </section>
          )}

          <section style={{ ...cardStyle, marginBottom: '22px' }}>
            <h2 style={{ fontSize: '1.25rem', margin: '0 0 4px' }}>Activity</h2>
            <p style={{ color: BRAND.muted, fontSize: '0.9rem', margin: '0 0 12px' }}>
              Most recent first — digital tutorials taken and worksheets printed for this student.
            </p>
            {activityFeed.length === 0 && <p style={{ color: BRAND.muted, margin: 0 }}>No activity recorded yet.</p>}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {activityFeed.map((item, idx) => (
                <div key={idx} style={{ alignItems: 'baseline', borderBottom: '1px solid rgba(45,64,66,0.1)', display: 'flex', flexWrap: 'wrap', gap: '10px', padding: '11px 4px' }}>
                  {item.type === 'attempt' ? (
                    <>
                      <span aria-hidden="true">🖥</span>
                      <span style={{ fontWeight: 800 }}>Digital tutorial {item.attempt.status === 'completed' ? 'completed' : 'started'}</span>
                      <span style={{ fontWeight: 700 }}>{item.tutorial.label}{item.tutorial.lessonPlanTitle ? ` — ${item.tutorial.lessonPlanTitle}` : ''}</span>
                      {item.tutorial.subject && <TagBadge>{item.tutorial.subject}</TagBadge>}
                      {item.attempt.totalAnswered > 0 && (
                        <span>
                          {item.attempt.correct}/{item.attempt.totalAnswered} correct (<StatPill value={item.attempt.accuracy} />)
                        </span>
                      )}
                      <button type="button" style={feedButtonStyle} onClick={() => openInTeacherView(item.tutorial, `/student-tutorial-player/${item.tutorial.studentTutorialId}?previewMode=1`)}>
                        Preview tutorial
                      </button>
                      {item.attempt.status === 'completed' && (
                        <button type="button" style={{ ...feedButtonStyle, background: 'rgba(255, 127, 80, 0.14)', borderColor: BRAND.coral }} onClick={() => openInTeacherView(item.tutorial, `/tutorial-report/${item.tutorial.studentTutorialId}`)}>
                          Progress report
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      <span aria-hidden="true">🖨</span>
                      <span style={{ fontWeight: 800 }}>{item.print.action === 'PRINT_ANSWER_KEY' ? 'Answer key printed' : 'Worksheet printed'}</span>
                      {item.count > 1 && <TagBadge tone="coral">×{item.count}</TagBadge>}
                      {item.tutorial && <span style={{ fontWeight: 700 }}>{item.tutorial.label}{item.tutorial.lessonPlanTitle ? ` — ${item.tutorial.lessonPlanTitle}` : ''}</span>}
                      <span style={{ color: BRAND.muted }}>by {item.print.teacherName}</span>
                      {item.tutorial && (
                        <button type="button" style={feedButtonStyle} onClick={() => openInTeacherView(item.tutorial, `/homework/${item.tutorial.homeworkId}`)}>
                          Preview worksheet
                        </button>
                      )}
                    </>
                  )}
                  <span style={{ color: BRAND.muted, fontSize: '0.85rem', marginLeft: 'auto', whiteSpace: 'nowrap' }}>{formatDateTime(item.when)}</span>
                </div>
              ))}
            </div>
          </section>

          {scaffolding && (
            <section style={{ ...cardStyle, borderTop: `6px solid ${BRAND.coral}`, marginBottom: '22px' }}>
              <h2 style={{ fontSize: '1.25rem', margin: '0 0 4px' }}>Scaffolding & interventions</h2>
              <p style={{ color: BRAND.muted, fontSize: '0.9rem', margin: '0 0 14px' }}>
                Current provision applied to every tutorial generated for this student.
              </p>

              <div style={{ display: 'grid', gap: '0 34px', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                <div>
                  <SettingRow label="SEN conditions">
                    {scaffolding.senConditions.length
                      ? scaffolding.senConditions.map((c) => <TagBadge key={c} tone="coral">{c}</TagBadge>)
                      : 'None recorded'}
                  </SettingRow>
                  <SettingRow label="EAL">
                    {scaffolding.eal ? `Yes${scaffolding.primaryLanguage ? ` · ${scaffolding.primaryLanguage}` : ''}` : 'No'}
                  </SettingRow>
                  {scaffolding.eal && (
                    <SettingRow label="Glossary language mode">
                      {scaffolding.glossaryLanguageMode === 'ENGLISH_TEXT_PRIMARY_GLOSSARY' ? 'English text · primary-language glossary' : 'Primary-language text · English glossary'}
                    </SettingRow>
                  )}
                  <SettingRow label="Audio narration">
                    {scaffolding.audioNarrationEnabled ? `On · ${scaffolding.audioNarrationSpeed}x speed` : 'Off'}
                  </SettingRow>
                </div>
                <div>
                  <SettingRow label="Custom scaffolding">
                    {scaffolding.noCustomScaffoldingRequired ? 'Not required' : 'Active'}
                  </SettingRow>
                  <SettingRow label="Custom tutorial structure">
                    {scaffolding.customTutorialStructure ? 'On' : 'Off'}
                  </SettingRow>
                  <SettingRow label="Key terms per matching page">
                    {scaffolding.keyTermsPerMatchingPage}
                  </SettingRow>
                  <SettingRow label="Tutorial font">
                    {scaffolding.tutorialFont}
                  </SettingRow>
                </div>
              </div>

              <h3 style={{ fontSize: '1rem', margin: '20px 0 8px' }}>Intervention history ({(scaffolding.history || []).length})</h3>
              {(scaffolding.history || []).length === 0 && (
                <p style={{ color: BRAND.muted, margin: 0 }}>No scaffolding changes recorded for this student.</p>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {(scaffolding.history || []).map((entry, idx) => {
                  const changes = diffHistoryEntry(entry);
                  return (
                    <div key={idx} style={{ background: BRAND.cream, border: '1.5px solid rgba(45,64,66,0.2)', borderRadius: '12px', padding: '12px 14px' }}>
                      <div style={{ alignItems: 'baseline', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        <TagBadge tone={entry.action === 'CREATED' ? 'teal' : 'coral'}>{entry.action}</TagBadge>
                        {entry.className && <TagBadge>{entry.className}</TagBadge>}
                        {entry.subject && <TagBadge>{entry.subject}</TagBadge>}
                        <span style={{ color: BRAND.muted, fontSize: '0.85rem', marginLeft: 'auto' }}>
                          {formatDateTime(entry.changedAt)} · by {entry.changedBy}
                        </span>
                      </div>
                      {entry.reason && <p style={{ fontSize: '0.9rem', fontWeight: 700, margin: '8px 0 0' }}>Reason: {entry.reason}</p>}
                      {entry.action !== 'CREATED' && changes.length > 0 && (
                        <ul style={{ fontSize: '0.88rem', lineHeight: 1.6, margin: '8px 0 0', paddingLeft: '18px' }}>
                          {changes.map((change) => (
                            <li key={change.label}>
                              <strong>{change.label}:</strong>{' '}
                              <span style={{ color: BRAND.muted, textDecoration: 'line-through' }}>{change.from}</span>
                              {' → '}
                              <strong>{change.to}</strong>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          <section style={{ marginBottom: '22px' }}>
            <h2 style={{ fontSize: '1.25rem', margin: '0 0 14px' }}>Assigned tutorials & performance ({tutorials.length})</h2>
            {tutorials.length === 0 && <p style={{ color: BRAND.muted }}>No tutorials have been assigned to this student yet.</p>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {tutorials.map((t) => (
                <div key={t.studentTutorialId} style={cardStyle}>
                  <div style={{ alignItems: 'baseline', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    <h3 style={{ fontSize: '1.05rem', margin: 0 }}>{t.label}</h3>
                    {t.lessonPlanTitle && <span style={{ fontWeight: 700 }}>{t.lessonPlanTitle}</span>}
                    {t.subject && <TagBadge>{t.subject}</TagBadge>}
                    {t.className && <TagBadge>{t.className}</TagBadge>}
                    <span style={{ color: BRAND.muted, fontSize: '0.85rem', marginLeft: 'auto' }}>Assigned {formatDate(t.assignedAt)}</span>
                  </div>

                  <div style={{ color: BRAND.muted, fontSize: '0.85rem', fontWeight: 600, margin: '8px 0 0' }}>
                    {t.durationMinutes ? `${t.durationMinutes} min · ` : ''}
                    Generation: {t.generationStatus}
                    {t.translationApplied ? ` · translated to ${t.contentLanguage}` : ''}
                    {t.customTutorialStructure ? ' · custom structure' : ''}
                    {t.completedAt ? ` · completed ${formatDate(t.completedAt)}` : ' · not yet completed'}
                  </div>

                  {(t.attempts || []).length > 0 && (
                    <table style={{ borderCollapse: 'collapse', marginTop: '12px', width: '100%' }}>
                      <thead>
                        <tr>
                          {['Attempt', 'Status', 'Correct', 'Accuracy', 'Extra time answers', 'Started', 'Completed'].map((h) => (
                            <th key={h} style={{ borderBottom: `2px solid ${BRAND.charcoal}`, color: BRAND.muted, fontFamily: 'Nunito, sans-serif', fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.05em', padding: '6px 10px', textAlign: 'left', textTransform: 'uppercase' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {t.attempts.map((a, idx) => (
                          <tr key={a.attemptId}>
                            <td style={{ borderBottom: '1px solid rgba(45,64,66,0.1)', fontWeight: 800, padding: '8px 10px' }}>#{t.attempts.length - idx}</td>
                            <td style={{ borderBottom: '1px solid rgba(45,64,66,0.1)', padding: '8px 10px' }}>{a.status === 'completed' ? 'Completed' : 'In progress'}</td>
                            <td style={{ borderBottom: '1px solid rgba(45,64,66,0.1)', padding: '8px 10px' }}>{a.correct}/{a.totalAnswered}</td>
                            <td style={{ borderBottom: '1px solid rgba(45,64,66,0.1)', padding: '8px 10px' }}><StatPill value={a.accuracy} /></td>
                            <td style={{ borderBottom: '1px solid rgba(45,64,66,0.1)', padding: '8px 10px' }}>{a.extraTimeAnswers || 0}</td>
                            <td style={{ borderBottom: '1px solid rgba(45,64,66,0.1)', padding: '8px 10px', whiteSpace: 'nowrap' }}>{formatDateTime(a.startedAt)}</td>
                            <td style={{ borderBottom: '1px solid rgba(45,64,66,0.1)', padding: '8px 10px', whiteSpace: 'nowrap' }}>{formatDateTime(a.completedAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ))}
            </div>
          </section>
        </>
      )}
    </SLTLayout>
  );
};

export default SLTStudentDetail;
